import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import * as React from 'react'

import { cn } from 'utils/StyleUtils'

type ScrollAreaProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Root
> & {
  /**
   * Which direction to allow scrolling.
   *
   * @default 'vertical'
   */
  direction?: 'vertical' | 'horizontal' | 'both'

  /**
   * Aria label for the scrollable viewport. When provided, the viewport is
   * exposed as a focusable region so keyboard and screen-reader users can
   * reach and scroll it.
   */
  viewportAriaLabel?: string
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    {
      className,
      children,
      direction = 'vertical',
      viewportAriaLabel,
      ...props
    },
    ref
  ) => (
    <ScrollAreaPrimitive.Root
      className={cn('relative overflow-hidden', className)}
      ref={ref}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        aria-label={viewportAriaLabel}
        className="size-full rounded-[inherit]"
        role={viewportAriaLabel ? 'region' : undefined}
        tabIndex={viewportAriaLabel ? 0 : undefined}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {direction !== 'horizontal' && <ScrollBar orientation="vertical" />}
      {direction !== 'vertical' && <ScrollBar orientation="horizontal" />}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
)

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-px',
      orientation === 'horizontal' &&
        'h-2.5 empty:hidden flex-col border-t border-t-transparent p-px',
      className
    )}
    orientation={orientation}
    ref={ref}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-[24px] bg-gray-8" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))

ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
